import type { SessionAnchor } from '@scrambl/shared'
import type { SessionSnapshot } from '@scrambl/shared/session-scope'

type Owner = SessionSnapshot
type RecordIdentity = Pick<SessionAnchor, 'type' | 'id'>
type Entry = { record: string; sessionId: string }
const MAX_RECORDS = 50

function storageKey(owner: Owner) {
  return owner.identity && !owner.signal.aborted
    ? `benny-record-sessions:v1:${owner.identity}`
    : null
}

function entries(owner: Owner): Entry[] {
  const key = storageKey(owner)
  if (!key) return []
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? '[]')
    return Array.isArray(value)
      ? value
          .filter(
            (item): item is Entry =>
              Boolean(item) &&
              typeof item.record === 'string' &&
              typeof item.sessionId === 'string',
          )
          .slice(0, MAX_RECORDS)
      : []
  } catch {
    return []
  }
}

/** Stores only conversation IDs, scoped to the full authenticated workspace identity. */
export function rememberRecordSession(
  owner: Owner,
  record: RecordIdentity | undefined,
  sessionId: string | null,
) {
  const key = storageKey(owner)
  if (!key || !record) return
  const recordKey = JSON.stringify([record.type, record.id])
  const remaining = entries(owner).filter(entry => entry.record !== recordKey)
  try {
    localStorage.setItem(
      key,
      JSON.stringify(
        (sessionId
          ? [{ record: recordKey, sessionId }, ...remaining]
          : remaining
        ).slice(0, MAX_RECORDS),
      ),
    )
  } catch {
    // Browsers that disable storage keep normal conversation history.
  }
}

export function readRecordSession(
  owner: Owner,
  record: RecordIdentity | undefined,
): string | undefined {
  if (!record) return undefined
  const key = JSON.stringify([record.type, record.id])
  return entries(owner).find(entry => entry.record === key)?.sessionId
}
